import axios from 'axios';
import { AnyAction, Dispatch } from 'redux';
import {
  setApiKeyList,
  setEmailDetail,
  setInvoiceSeting,
  setProfileSettingsData,
  setSettingLoader,
  setSubscriptionList,
  setProjectTemplateList,
  setProjectTemplateDetails,
  setReferralDetails,
  setNotificationsSettingsDetails,
  setTaskTemplateData,
  setTaskTemplateList,
  setSettingIntegrationData,
  setSecurityAccessList
} from '../actions/settingActions';
import { getAPIErrorReason, getCurrentDomain, isDev, isEmail, isEmpty } from '../helpers/common';
import UserPreferenceSingleton from '../helpers/userPreferenceSingleton';
import {
  AppSettingDetailsInterface,
  BrandDomainDetailsInterface,
  EmailDetailInterface,
  InviteNewUserInterface,
  InvoicingDetailsInterface,
  NotificationSettingDetailsInterface,
  ProfileDataInterface,
  ProjectTemplateDetailsInterface,
  TaskTemplateDataInterface
} from '../interfaces/SettingsInterface';
import { WorkspaceInterface } from '../interfaces/WorkspaceInterface';
import { REACT_APP_SUBSCRIPTION_PRICE_ID, REACT_APP_TEAMCAMP_APIURL } from '../global/environment';
import { trackAnalyticActivity } from './analyticsService';
import { captureException } from './logService';
import { setUserProfileData } from '../actions/authActions';
import { DEFAULT_DATE_FORMAT } from '../global/constants';
import { StatusListInterface } from '../interfaces/ProjectInterface';
import { WORKSPACE_ANALYTICS } from '../global/analyticsConstants';
import { rootStore } from '../mobx/rootStore';

/**
 * Invites a new user to join the team/company by sending an email invitation
 *  @param {*}
 */
export const inviteNewUser: any = (payloadData: InviteNewUserInterface) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Check for email empty and valid email validation
    if (isEmpty(payloadData?.email)) {
      dispatchSettingError('Please enter email!');
      return;
    } else if (!isEmail(payloadData?.email)) {
      dispatchSettingError('Please enter valid email!');
      return;
    }
    // Set payload data
    const payload = {
      ...payloadData,
      isDev: isDev()
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/company/InviteUsers`, payload);
    if (response?.data?.error) {
      dispatchSettingError(response.data.error);
      return true;
    } else if (response) {
      dispatchSettingSuccess(response.data.message);
      return response?.data?.data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to invite user please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Removes a invite user from the team
 * @param {*}
 */
export const deleteInvitedUser: any = (userId: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
    if (!userDetails?.id) {
      dispatchSettingError('Current user not found');
      return false;
    }
    const params = {
      userId: userDetails?.id,
      deleteUserId: userId
    };
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/removeCompanyUser`, {
      params
    });
    if (response) {
      trackAnalyticActivity(WORKSPACE_ANALYTICS.USER_REMOVED);
      dispatchSettingSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete invited user please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Fetches user profile settings data from the API
 * @param {*}
 */
export const getProfileSettingData: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/User/getUserDetails`);
    const { data } = response;
    if (data) {
      const updatedData = {
        ...data,
        dateFormat: !isEmpty(data?.dateFormat) ? data?.dateFormat : DEFAULT_DATE_FORMAT
      };
      dispatch(setProfileSettingsData(updatedData));
      dispatch(setEmailDetail(updatedData));
      dispatch(setUserProfileData(updatedData));
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get profile setting data please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc setting - Updates user profile information

 * @param {*}
 */
export const updateProfileSetting: any =
  (payloadData: ProfileDataInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      if (isEmpty(payloadData?.name)) {
        dispatchSettingError('Please enter name!');
        return false;
      }
      const payload = {
        ...payloadData
      };
      const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/User/updateProfile`, payload);
      if (response) {
        if (response.data.message) {
          dispatchSettingSuccess(response.data.message);
          return true;
        } else if (response.data.error) {
          dispatchSettingError(response.data.error);
          return false;
        }
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to update profile setting data please try again';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return false;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc  Updates user's email address after validating current password
 * @param {*}
 */
export const updateEmailData: any = (payloadData: EmailDetailInterface) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    if (isEmpty(payloadData?.email)) {
      rootStore.alertNotificationStore.setErrorMessage('Please enter email.');
      return;
    } else if (isEmpty(payloadData?.password)) {
      rootStore.alertNotificationStore.setErrorMessage('Please enter password.');
      return;
    }
    const payload = {
      ...payloadData
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/User/changeEmail`, payload);
    if (response) {
      dispatchSettingSuccess(response.data.message);
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to update email details please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Changes the role of a user to admin in the workspace
 * @param {*}
 */
export const changeUserAdmin: any = (user_id: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    if (isEmpty(user_id)) {
      dispatchSettingError('Invalid user selected');
      return false;
    }
    const payload = {
      target_user_id: user_id
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/company/putUserAdmin`, payload);
    if (!isEmpty(response?.data?.error)) {
      dispatchSettingError(response?.data?.error);
    } else if (response?.data?.message) {
      dispatchSettingSuccess(response?.data?.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to change user role please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Handles the Auth redirect flow for connecting a Stripe account to a workspace
 * @param {*}
 */
export const stripeRedirect: any =
  (
    paramsObj: {
      user_id: string;
      account_id: string;
      install_signature: string;
      stripe_user_id: string | undefined;
      state: string;
      livemode: boolean;
    },
    workspaceId: string
  ) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Prepare parameters for the API call
      const params: {
        user_id: string;
        account_id: string;
        state?: string;
        install_signature: string;
        workspace_id: string;
        stripe_user_id: string;
        livemode: boolean;
      } = {
        user_id: paramsObj?.user_id || '',
        account_id: paramsObj?.account_id || '',
        state: paramsObj?.state,
        install_signature: paramsObj?.install_signature || '',
        workspace_id: workspaceId,
        stripe_user_id: paramsObj?.stripe_user_id || '',
        livemode: paramsObj?.livemode
      };
      // Make API call to complete Stripe connection
      const response: any = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/stripe/stripeRedirect`, {
        params
      });
      // Handle error response from API
      if (response.data.error) {
        dispatchSettingError(response.data.error);
        return false;
      }
      return true;
    } catch (e) {
      // Handle any errors during the process
      const error = getAPIErrorReason(e) || 'Unable to set stripe data';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return false;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - Disconnects Stripe payment integration from the specified workspace
 * @param {*}
 */
export const disconnectStripe: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to disconnect Stripe account
    const response: any = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/stripe/disconnectStripeAccount`);
    // Check if there's an error in the response
    if (response.data.error) {
      dispatchSettingError(response?.data?.error);
      return false;
    }
    return true;
  } catch (e) {
    // Handle any errors that occur during the process
    const error = getAPIErrorReason(e) || 'Unable to disconnect stripe account';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - get API Key List
 * @param {*}
 */
export const getApiKeyList: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/apiKey/getApiKeyList`);
    // Extract data from response
    const { data } = response;
    // If data exists, update Redux store with API key list
    if (data) {
      dispatch(setApiKeyList(data));
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get API key list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting -  Creates a new API key
 * @param {*}
 */
export const createApiKey: any = (title: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));

    // Validate if title is provided
    if (isEmpty(title)) {
      dispatchSettingError('Please enter api key name.');
      return;
    }
    // Prepare payload for API request
    const payload = {
      title
    };
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/apiKey/createApiKey`, payload);
    // Handle API response
    if (!isEmpty(response?.data?.error)) {
      dispatchSettingError(response?.data?.error);
    } else if (response) {
      dispatchSettingSuccess(response?.data?.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to create API key please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Deletes an API key by making a DELETE request to the server
 * @param {*}
 */
export const deleteApiKey: any = (apiKey_Id: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Prepare parameters for the API request
    const params = {
      apyKeyId: apiKey_Id
    };
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/apiKey/deleteApiKey`, { params });
    // Handle error response from the API
    if (!isEmpty(response?.data?.error)) {
      dispatchSettingError(response?.data?.error);
    } else if (response) {
      dispatchSettingSuccess(response?.data?.message);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to Delete key please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Handles Figma Auth redirect callback
 * @param {*}
 */
export const figmaConnectRedirect: any =
  (paramsObj: { code: string; state: string }) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));

      // Prepare parameters for the API call
      const params: {
        code: string;
      } = {
        code: paramsObj?.code || ''
      };
      const response: any = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/figma/figmaRedirect`, {
        params
      });

      // Handle error response from the API
      if (response.data.error) {
        dispatchSettingError(response.data.error);
        return false;
      }

      return true;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to set figma redirect details';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return false;
    } finally {
      // Always hide loading state when done
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Updates company app settings
 * @param {*}
 */
export const updateAppsSettings: any =
  (payload: AppSettingDetailsInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      // Make POST request to update app settings
      const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/company/putAppsSetting`, payload);
      if (response) {
        dispatchSettingSuccess('Updated successfully!');
        return true;
      }
    } catch (e) {
      // Handle error case
      const error = getAPIErrorReason(e) || 'Unable to update apps settings please try again';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return false;
    }
  };

/**
 * @desc Setting - Fetch Subscription List
 * @param {*}
 */
export const getSubscriptionList: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to fetch active and past subscriptions
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/admin/subscription/getActivePastSubscriptions`);
    const { data } = response;
    // If data is received successfully, update the subscription list in store
    if (data) {
      dispatch(setSubscriptionList(data));
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get subscription list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Fetches and updates invoice settings for a specific workspace
 * @param {*}
 */
export const getInvoicingSettingsData: any = (workspace_id: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to fetch invoice settings for the given workspace
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/getPutInvoiceSetting/${workspace_id}`);
    const { data } = response;
    if (data) {
      // Update Redux store with fetched invoice settings
      dispatch(setInvoiceSeting(data));
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get Invoicing details please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting -  Updates invoice settings for a workspace with provided details
 * @param {*}
 */
export const updateInvoicingSettings: any =
  (workspace_id: string, payload: InvoicingDetailsInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Get current user details and add userId to the payload
      const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
      payload.userId = userDetails?.id;
      const response = await axios.put(
        `${REACT_APP_TEAMCAMP_APIURL}/company/getPutInvoiceSetting/${workspace_id}`,
        payload
      );
      if (response) {
        // Show success message and return true on successful update
        dispatchSettingSuccess(response.data.message);
        return true;
      }
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to update invoicing details please try again';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return false;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - Creates a new project template with the given workspace ID, name and description
 * @param {*}
 */
export const createProjectTemplate: any =
  (workspace_id: string, name: string, description: string) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Prepare payload with required template details
      const payload = {
        name: name,
        companyId: workspace_id,
        description: description
      };
      const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/postProjectTemplate`, payload);
      if (response) {
        // Check if there's any error in response
        if (!isEmpty(response?.data?.error)) {
          dispatchSettingError(response?.data?.error);
          return false;
        } else {
          dispatchSettingSuccess('Project template created succesfully!');
          return true;
        }
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to create Project Template please try again';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return false;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - Retrieves list of project templates for a workspace, optionally formatted for project creation
 * @param {*}
 */
export const getProjectTemplateList: any =
  (workspace_id: string, isCreateProject = false) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));

      // Fetch project templates for the given workspace
      const response = await axios.get(
        `${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/getProjectTemplateList/${workspace_id}`
      );
      const { data } = response;
      if (data) {
        if (isCreateProject) {
          // Format data for project creation dropdown if isCreateProject is true
          const updatedData = data?.map((x: { name: string }) => {
            return { ...x, value: x?.name };
          });
          dispatch(setProjectTemplateList(updatedData || []));
          return updatedData;
        } else {
          dispatch(setProjectTemplateList(data));
          return data;
        }
      }
      return undefined;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to get Project Template list';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return undefined;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - Fetches detailed information about a specific project template and
 *  enhances it with workspace user data
 * @param {*}
 */
export const getProjectTemplateDetails: any =
  (workspace: WorkspaceInterface, templateId: string, returnUpdatedData = true) =>
  async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Fetch specific project template details
      const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/getProjectTemplate/${templateId}`);
      const { data } = response;
      if (data) {
        if (!returnUpdatedData) return data;
        // Map project users with workspace users
        const projectUsers = data?.projectUsers?.map((x: any) => {
          const user = workspace?.users?.find((y: any) => x?.['_id'] === y?.id);
          return user;
        });
        // Find default status from status data
        const defaultStatus = data?.statusData?.find((status: StatusListInterface) => status?.Default);
        // Format and enhance template data with additional information
        const updatedData = {
          ...data,
          id: data?.['_id'],
          users: projectUsers,
          defaultStatus: defaultStatus || data?.Status?.[0] || {}
        };
        dispatch(setProjectTemplateDetails(updatedData || {}));
        return updatedData;
      }
      return undefined;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to get Project Template details';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return undefined;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - Updates an existing project template with new details
 * @param {*}
 */
export const saveProjectTemplateDetails: any =
  (payload: ProjectTemplateDetailsInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Update project template with new details
      const response = await axios.put(
        `${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/putProjectTemplate/${payload?.['_id']}`,
        { ...(payload || {}) }
      );
      const { data } = response;
      if (data) {
        dispatchSettingSuccess('Template updated succesfully');
        return data;
      }
      return undefined;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to save project template details';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return undefined;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - Removes a project template from the system
 * @param {*}
 */
export const deleteProjectTemplate: any = (templateId: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Delete project template by ID
    const response = await axios.delete(
      `${REACT_APP_TEAMCAMP_APIURL}/projectTemplate/deleteProjectTemplate/${templateId}`
    );
    if (!isEmpty(response?.data?.error)) {
      dispatchSettingError(response?.data?.error);
    } else if (response) {
      dispatchSettingSuccess(response?.data);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to Delete template. please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting -  Fetches the user's referral code
 * @param {*}
 */
export const getUserReferCode: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to get user's referral code
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/User/getUserRefCode`);
    const { data } = response;
    // Return the referral code if data exists
    if (data) {
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get user refer code. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting ->  Fetches and returns the user's referral details
 * @param {*}
 */
export const getReferralDetails: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/User/getReferralDetails`);
    const { data } = response;
    // If data exists, update the store with referral details and return the data
    if (data) {
      dispatch(setReferralDetails(data));
      return data;
    }
    return undefined;
  } catch (e) {
    // Handle any errors that occur during the API call
    const error = getAPIErrorReason(e) || 'Unable to get Referral Details. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Adds a custom domain to the Vercel project
 * @param {*}
 */
export const addDomainInVercelProject: any = (domain: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Validate if domain is provided
    if (isEmpty(domain)) return dispatchSettingError('Please valid domain URL!');
    // Make API call to add domain to Vercel project
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/branding/addVercelDomain`, { domain });
    const { data } = response;
    if (data) {
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to add domain in vercel. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting -  Fetches the current branding details from the API
 * @param {*}
 */
export const getBrandingDetails: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Fetch branding details from the API
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/branding/getBrandingDetails`);
    const { data } = response;
    if (data) {
      return data;
    }
  } catch (e) {
    // Handle error and log it for monitoring
    const error = getAPIErrorReason(e) || 'Unable to get branding details. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Updates the branding details with new information
 * @param {*}
 */
export const updateBrandingDetails: any =
  (payload: BrandDomainDetailsInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Update branding details through API
      const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/branding/updateBrandingDetails`, payload);
      const { data } = response;
      if (data) {
        // Show success message and return updated data
        dispatchSettingSuccess('Branding data updated successfully');
        return data;
      }
    } catch (e) {
      // Handle error and log it for monitoring
      const error = getAPIErrorReason(e) || 'Unable to update branding details. Please try again!';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return undefined;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - Fetches domain configuration details from the server for a given domain
 * @param {*}
 */
export const getDomainConfigurationDetails: any = (domain: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to get domain configuration details
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/branding/getDomainConfiguration/${domain}`);
    const { data } = response;
    // Return the domain configuration data if available
    if (data) {
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get domain config. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting -  Removes a domain from Vercel project and updates the configuration
 * @param {*}
 */
export const removeVercelDomain: any = (domain: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Validate domain parameter
    if (isEmpty(domain)) return dispatchSettingError('Please valid domain URL!');
    // Make API call to remove domain from Vercel
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/branding/removeDomain/${domain}`);
    const { data } = response;
    // If successful, show success message and return data
    if (data) {
      dispatchSettingSuccess('Domain removed successfully');
      return data;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove domain in vercel. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Disconnects the Figma account integration from the current workspace
 * @param {*}
 */
export const disconnectFigmaAccount: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to disconnect Figma account
    const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/figma/disconnectFigma`);
    // If successful, show success message and return true
    if (response) {
      dispatchSettingSuccess(response.data);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to disconnect figma account please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Fetches user notification settings
 * @param {*}
 */
export const getNotificationSettingDetails: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to fetch notification settings
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/User/userNotificationSettings`);
    const { data } = response;
    if (data) {
      // Update Redux store with notification settings and set updated flag to false
      dispatch(setNotificationsSettingsDetails({ ...data, updated: false }));
      return data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get notification setting details. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Updates user notification settings
 * @param {*}
 */
export const updateNotificationSettingDetails: any =
  (payload: NotificationSettingDetailsInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Make API call to update notification settings with new payload
      const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/User/userNotificationSettings`, payload);
      const { data } = response;
      if (data) {
        // Show success message if update was successful
        dispatchSettingSuccess('Notifications setting updated!');
        return true;
      }
      return false;
    } catch (e) {
      // Handle API errors and log them for monitoring
      const error = getAPIErrorReason(e) || 'Unable to update notification setting details. Please try again!';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return false;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - template -  Creates a new task template with the provided payload data
 * @param {*}
 */
export const createNewTaskTemplate: any =
  (payload: TaskTemplateDataInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Make API call to create task template
      const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate`, payload);
      const { data } = response;
      if (data) {
        return true;
      }
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to create task template. Please try again!';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return undefined;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting - template -  Retrieves the list of all task templates
 * @param {*}
 */
export const getTaskTemplateList: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to get task templates
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate`);
    const { data } = response;
    if (data) {
      // Update Redux store with task template list
      dispatch(setTaskTemplateList(data?.data));
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get task template list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting -template - Deletes a specific task template by its ID
 * @param {*}
 */
export const deleteTaskTemplate: any = (templateId: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to delete task template
    const response = await axios.delete(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate/${templateId}`);
    if (!isEmpty(response?.data?.error)) {
      // Handle API error response
      dispatchSettingError(response?.data?.error);
    } else if (response) {
      // Show success message
      dispatchSettingSuccess(response?.data);
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to delete task template. please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting -template - Updates the details of a task template
 * @param {*}
 */
export const updateTaskTemplateDetails: any =
  (taskId: string, payload: TaskTemplateDataInterface) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Make API call to update task template
      const response = await axios.put(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate/${taskId}`, payload);
      const { data } = response;
      if (data) {
        // Show success message and return updated data
        dispatchSettingSuccess(' Task Template updated succesfully');
        return data;
      }
      return undefined;
    } catch (e) {
      // Handle error and show error message
      const error = getAPIErrorReason(e) || 'Unable to update task template details';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return undefined;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting -template -  Fetches the details of a task template and processes the response
 * @param {*}
 */
export const getTaskTemplateDetails: any =
  (workspace: WorkspaceInterface, templateId: string) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Make API call to get task template details
      const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/taskTemplate/${templateId}`);
      const { data } = response?.data;
      if (data) {
        // Map assignee IDs to user objects from workspace
        const assignedUsers = data?.Assignee?.map((assigneeId: string) =>
          workspace?.users?.find((user: any) => user.id === assigneeId)
        );
        // Transform API response into required format
        const updatedData = {
          templateName: data?.Title,
          description: data?.Description,
          name: data?.TaskTitle,
          priority: data?.Priority || 0,
          users: assignedUsers,
          Files: data?.Attachment,
          _id: data?.['_id']
        };
        dispatch(setTaskTemplateData(updatedData));
        return true;
      }
      return undefined;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to get Task Template details';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return undefined;
    } finally {
      dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc  Gets the Stripe subscription URL for the current workspace
 * @param {*}
 */
export const getSubscriptionUrl: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Prepare payload with price ID and success URL for Stripe subscription
    const payload = {
      priceId: REACT_APP_SUBSCRIPTION_PRICE_ID,
      successUrl: `https://${getCurrentDomain()}/settings/subscription?status=success`
    };
    // Make API call to get Stripe subscription URL
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/stripe/getSubscriptionUrl`, payload);
    // Return subscription URL data if available
    if (response?.data) {
      return response?.data;
    }
    return undefined;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get subscription url';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Invoice - Fetches and updates the company's Stripe integration status for the given workspace
 * @param {*}
 */
export const getCompanySettingIntegration: any = (workSpace_Id: string) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));
    // Make API call to get company's Stripe integration status
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/company/integrationStatus`, {
      headers: { workspace_id: workSpace_Id }
    });
    const { data } = response;
    if (data) {
      // Update Redux store with integration data
      dispatch(setSettingIntegrationData(data));
      return data;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || `Unable to get Company Stripe Account Status please try again`;
    captureException(error);
    dispatchSettingError(error);
    return undefined;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc sentry connect
 * @param {*}
 */
export const sentryAccountConnect: any =
  (payload: { installationId: string; code: string }) => async (dispatch: Dispatch<AnyAction>) => {
    try {
      dispatch(setSettingLoader(true));
      // Make API call to bind Sentry account
      const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/sentry/bind`, payload);
      // Return true if connection was successful
      if (response) return true;
      return false;
    } catch (e) {
      const error = getAPIErrorReason(e) || 'Unable to add sentry account! please try again';
      captureException(error, 'settings');
      dispatchSettingError(error);
      return false;
    } finally {
      await dispatch(setSettingLoader(false));
    }
  };

/**
 * @desc Setting -  Fetches and returns a list of all active user sessions
 * @param {*}
 */
export const getSessionList: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    dispatch(setSettingLoader(true));

    // Make API call to fetch all active sessions
    const response = await axios.get(`${REACT_APP_TEAMCAMP_APIURL}/auth/getSessions`);
    const { data } = response;
    if (data) {
      // Update Redux store with the fetched session list
      dispatch(setSecurityAccessList(data));
      return true;
    }
    return false;
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to get session list. Please try again!';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  } finally {
    dispatch(setSettingLoader(false));
  }
};

/**
 * @desc Setting - Revokes all active sessions for the current user
 * @param {*}
 */
export const removeAllSession: any = () => async (dispatch: Dispatch<AnyAction>) => {
  try {
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/revokeAllSessions`);
    if (response) {
      // Show success message when all sessions are revoked
      dispatchSettingSuccess('remove all session successfully!');
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove all session list please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  }
};

/**
 * @desc Setting - Revokes a specific session using the provided session ID
 * @param {*}
 */
export const removeSession: any = (payload: { sessionId: string }) => async (dispatch: Dispatch<AnyAction>) => {
  try {
    const response = await axios.post(`${REACT_APP_TEAMCAMP_APIURL}/auth/revokeSession`, payload);
    if (response) {
      // Show success message when the specific session is revoked
      dispatchSettingSuccess('Session revoked successfully!');
      return true;
    }
  } catch (e) {
    const error = getAPIErrorReason(e) || 'Unable to remove session list please try again';
    captureException(error, 'settings');
    dispatchSettingError(error);
    return false;
  }
};

export function dispatchSettingError(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

export function dispatchSettingSuccess(msg: string) {
  rootStore.alertNotificationStore.setSuccessMessage(msg);
}
